import React, { useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function Loader() {
  return (
    <div className="grid min-h-screen place-items-center bg-slate-50">
      <div
        className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-[#720e1e]"
        aria-label="Loading"
      />
    </div>
  );
}

export default function MarketingRoute({ children }) {
  const location = useLocation();
  const {
    user,
    isAuthenticated,
    loading,
    authReady,
    adminAccessLoading,
    adminAccess,
  } = useAuth();
  const hasGrantedAccessRef = useRef(false);

  const roles = (adminAccess?.roles || []).map((role) =>
    String(role).trim().toLowerCase(),
  );
  const allowed =
    adminAccess?.is_marketing === true ||
    roles.some((role) =>
      [
        "superadmin",
        "admin",
        "marketing_lead",
        "marketing",
        "marketing_viewer",
      ].includes(role),
    );

  if (user && allowed) {
    hasGrantedAccessRef.current = true;
  }

  const accessCheckPending = loading || !authReady || adminAccessLoading;

  // Keep an already-authorised Marketing workspace mounted while Supabase
  // refreshes the session or re-checks staff access in the background. Replacing
  // it with a loader would destroy open forms, comments and scroll position.
  if (accessCheckPending) {
    return hasGrantedAccessRef.current ? children : <Loader />;
  }

  if (!isAuthenticated || !user) {
    hasGrantedAccessRef.current = false;
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (allowed) {
    hasGrantedAccessRef.current = true;
    return children;
  }

  // Preserve the last confirmed access during a temporary failed role refresh.
  // Backend RLS still protects every Marketing query.
  if (hasGrantedAccessRef.current && adminAccess == null) {
    return children;
  }

  hasGrantedAccessRef.current = false;
  return <Navigate to="/dashboard" replace />;
}
